import {
  IonBadge,
  IonButton,
  IonCard,
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonSpinner,
  IonTabBar,
  IonTabButton,
  useIonModal
} from "@ionic/react";
import { addOutline } from "ionicons/icons";
import { ReactComponent as BotIcon } from "../assets/graphics/smart_toy.svg";
import { useEffect, useState } from "react";
import { SetupBot } from "./SetupBot";
import { useAuthStore } from "../stores/auth.store";
import { useBotsStore } from "../stores/bots.store";
import Header from "../components/Header";
import { useHistory } from "react-router";
import { useHelpStore } from "../stores/help.store";
import HelpButton from "../components/HelpButton";

const Bots: React.FC = () => {
  // eslint-disable-next-line
  const [uid, loading] = useAuthStore(state => [
    state.user?.uid,
    state.loading
  ]);
  const history = useHistory();
  const [bots, selectedMode, setSelectedMode] = useBotsStore(state => [
    state.bots.filter(b => b.user === uid),
    state.selectedMode,
    state.setSelectedMode
  ]);
  const [activeTab, setActiveTab] = useState(selectedMode);
  const { setActiveHelpId, activeHelpId } = useHelpStore(state => state);

  useEffect(() => {
    if (activeTab === "simulation") {
      setActiveHelpId("simulated_bots");
    } else if (activeTab === "backtest") {
      setActiveHelpId("backtest_bots");
    } else {
      setActiveHelpId("live_bots");
    }
  }, [activeTab, setActiveHelpId]);

  const title =
    activeTab === "simulation"
      ? "Simulated Bots"
      : activeTab === "backtest"
      ? "Backtest Bots"
      : activeTab === "live"
      ? "Live Bots"
      : "Bots";

  const activeBots = bots.filter(bot => bot.mode === activeTab && bot.active);
  const upcomingBots = bots.filter(
    bot =>
      bot.mode === activeTab && !bot.provisioned && !bot.active && !bot?.payOut
  );
  const pastBots = bots.filter(
    bot => bot?.mode === activeTab && bot.provisioned && !bot.active
  );

  const [presentSetupBot, dismissSetupBot] = useIonModal(SetupBot, {
    mode: activeTab,
    navigateTo: (path: string) => history.push(path),
    onDismiss: () => dismissSetupBot()
  });

  return (
    <IonPage>
      <Header title="My Bots" />
      <IonContent fullscreen scrollY={false}>
        <div className="h-full bg-primary-90 flex justify-center">
          <div className="max-w-xl">
            <IonTabBar
              onIonTabsDidChange={e => {
                setActiveTab(e.detail.tab);
                setSelectedMode(e.detail.tab);
              }}
              className="m-2 bg-black rounded-lg px-2"
            >
              <IonTabButton
                tab="simulation"
                className={`text-base rounded-lg " ${
                  activeTab === "simulation"
                    ? "bg-primary-50 text-white"
                    : "bg-black"
                }`}
              >
                <IonLabel>Simulated</IonLabel>
              </IonTabButton>
              <IonTabButton
                tab="backtest"
                className={`text-base rounded-lg " ${
                  activeTab === "backtest"
                    ? "bg-primary-50 text-white"
                    : "bg-black"
                }`}
              >
                <IonLabel>Backtest</IonLabel>
              </IonTabButton>

              <IonTabButton
                tab="live"
                className={`text-base rounded-lg " ${
                  activeTab === "live" ? "bg-primary-50 text-white" : "bg-black"
                }`}
              >
                <div className="flex justify-center items-center">
                  {/* <IonIcon icon={lockClosedOutline} /> */}
                  <IonLabel>Live</IonLabel>
                </div>
              </IonTabButton>
            </IonTabBar>

            <div className="flex flex-col h-4/6">
              <div className="grow">
                <div className="flex items-center justify-between pt-2">
                  <div className="text-3xl font-bold px-4 py-2">{title}</div>
                  <HelpButton id={activeHelpId} />
                </div>
                {activeTab !== "live" && (
                  <div className="px-4">
                    Learn and experiment with your
                    {activeTab === "simulation" && " Simulated "}
                    {activeTab === "backtest" && " Backtest "}
                    Bot
                    {activeTab === "simulation" && " in real time "}
                    {activeTab === "backtest" && " on historical data "}
                    with Zero Risk.
                  </div>
                )}
                {activeTab === "live" && (
                  <div className="px-4">
                    Trade with your Live bot using API key integration and earn
                    real profits.
                  </div>
                )}
                <IonCard
                  className="my-4 bg-primary-80 text-white"
                  button
                  disabled={activeBots?.length === 0}
                  routerLink={`/botList/${activeTab}/active`}
                >
                  <div className="px-4 py-2 flex justify-between">
                    <div className="text-lg flex items-center justify-start w-full">
                      <BotIcon className="p-1 active-fill bg-primary-90" />
                      <div className="font-semibold px-2">Active</div>
                    </div>
                    <IonBadge
                      slot="start"
                      className="text-base px-4 rounded-lg bg-primary-90 text-primary-30"
                    >
                      {loading ? (
                        <div className="ion-text-center">
                          <IonSpinner />
                        </div>
                      ) : (
                        activeBots?.length || 0
                      )}
                    </IonBadge>
                  </div>
                  <div className="px-4 pb-2">
                    Bots that are currently trading.
                  </div>
                </IonCard>
                {activeTab === "simulation" && (
                  <IonCard
                    className="my-4 bg-primary-80 text-white"
                    button
                    disabled={upcomingBots?.length === 0}
                    routerLink={`/botList/${activeTab}/upcoming`}
                  >
                    <div className="px-4 py-2  flex justify-between">
                      <div className="text-lg flex items-center justify-start w-full">
                        <BotIcon className="p-1 upcoming-fill bg-primary-90" />
                        <div className="font-semibold px-2">Upcoming</div>
                      </div>
                      <IonBadge
                        slot="start"
                        className="text-base px-4 rounded-lg bg-primary-90 text-primary-30"
                      >
                        {loading ? (
                          <div className="ion-text-center">
                            <IonSpinner />
                          </div>
                        ) : (
                          upcomingBots?.length || 0
                        )}
                      </IonBadge>
                    </div>
                    <div className="px-4 pb-2">
                      Bots that are scheduled to trade.
                    </div>
                  </IonCard>
                )}
                <IonCard
                  className="my-4 bg-primary-80 text-white"
                  button
                  disabled={pastBots?.length === 0}
                  routerLink={`/botList/${activeTab}/past`}
                >
                  <div className="px-4 py-2 flex justify-between">
                    <div className="text-lg flex items-center justify-start w-full">
                      <BotIcon className="p-1 past-fill bg-primary-90" />
                      <div className="font-semibold px-2">Past</div>
                    </div>
                    <IonBadge
                      slot="start"
                      className="text-base px-2 rounded-lg bg-primary-90 text-primary-30"
                    >
                      {loading ? (
                        <div className="ion-text-center">
                          <IonSpinner />
                        </div>
                      ) : (
                        pastBots?.length || 0
                      )}
                    </IonBadge>
                  </div>
                  <div className="px-4 pb-2">
                    Bots that have completed trading.
                  </div>
                </IonCard>
              </div>
              <div className={`${activeTab !== "simulation" && "pt-20"}`}>
                <IonButton
                  expand="block"
                  color="primary"
                  className="px-2 h-12 text-base rounded-xl mx-2 capitalize"
                  onClick={() => presentSetupBot()}
                  disabled={
                    (activeTab === "live" && activeBots?.length >= 1) ||
                    (activeTab === "simulation" && activeBots?.length === 3)
                  }
                >
                  {`Create ${activeTab} Bot`}
                  <IonIcon slot="end" icon={addOutline} />
                </IonButton>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Bots;
